// @ts-nocheck

import React, { useCallback, useContext, useEffect } from "react";
import cn from "classnames";
import Skeleton from "react-loading-skeleton";

import Icon from "../../../brand/Icon";
import { TextFieldProps } from "./TextField.props";
import Label from "../Label";
import FieldMessage from "../FieldMessage";

import globalFormStyles from "../global.formElements.module.scss";
import styles from "./TextField.module.scss";

import { FormContext } from "../../Form";

/**
 * @deprecated Use `import { Textbox } from capitalrx-components-2.0/lib/forms` instead.
 */
const TextField: React.FC<TextFieldProps> = ({
  id,
  labelText,
  placeholder,
  onChange,
  onFocus,
  onKeyPress,
  onPaste,
  onBlur,
  tabIndex,
  disabled = false,
  inputClassName,
  containerClassName,
  helperText,
  name,
  validate,
  type = "text",
  autocomplete = true,
  icon,
  useAsterisk,
  defaultValue,
  value,
  isLoading,
  maxLength,
  minNumber,
  pattern,
  testId,
  autoFocus = false,
}: TextFieldProps): JSX.Element => {
  // only get this specific text fields error object
  const {
    formState: { errors, isFormLoading },
    register,
    unregister,
  } = useContext(FormContext);

  const hasErrors = disabled ? undefined : errors;
  const isInvalid = errors && !disabled ? Object.keys(errors)?.length > 0 && errors[name] : false;

  const hasValidation = disabled ? undefined : validate;
  const registerField = register(name, hasValidation);

  const inputContainerClasses = cn(styles["form-field-container"], { disabled: disabled }, containerClassName);
  const inputClasses = cn(styles["text-field"], inputClassName, { [styles["invalid-field"]]: isInvalid });

  const handleInputChange = useCallback(
    // eslint-disable-next-line
    (ev: any): void => {
      registerField.onChange(ev);
      onChange && onChange(ev);
    },
    [onChange, registerField],
  );

  const handleBlurChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      onBlur && onBlur(ev);
      registerField.onBlur(ev);
    },
    [onBlur, registerField],
  );

  // Removes validation requirements on a disabled field
  useEffect(() => {
    if (disabled) {
      unregister(name);
    }
  }, [disabled, name]);

  const IconComponent = useCallback(() => {
    return icon ? <Icon id={id} name={icon} size="small" color="black" /> : null;
  }, [id, icon]);

  let component = null;

  const testVal = testId ? testId : `test__${id}`;

  if (isLoading || isFormLoading) {
    component = <Skeleton height={40} />;
  } else {
    component = (
      <div className={globalFormStyles["field-wrapper"]}>
        <IconComponent />
        <input
          {...registerField}
          aria-invalid={isInvalid ? "true" : "false"}
          aria-label={labelText}
          id={id}
          data-testid={testVal}
          type={type}
          placeholder={placeholder}
          tabIndex={tabIndex}
          disabled={disabled}
          onBlur={(e) => handleBlurChange(e)}
          onChange={(e): void => handleInputChange(e)}
          onFocus={onFocus}
          onKeyPress={onKeyPress}
          onPaste={onPaste}
          className={inputClasses}
          autoComplete={autocomplete ? "on" : "off"}
          defaultValue={defaultValue}
          value={value}
          maxLength={maxLength}
          pattern={pattern}
          min={minNumber}
          autoFocus={autoFocus}
        />
      </div>
    );
  }

  return (
    <div className={inputContainerClasses}>
      {labelText && (
        <Label id={id} label={labelText} disabled={disabled} isLoading={isLoading} validate={validate} useAsterisk={useAsterisk} />
      )}
      {component}
      <FieldMessage id={id} helperText={helperText} errors={hasErrors} name={name} isLoading={isLoading} />
    </div>
  );
};

/**
 * @deprecated
 */
export default TextField;
